/**
 * The headless sign-in: our page asks the server for a WorkOS Magic Auth
 * code (WorkOS emails it) and redeems it for the same one-time pairing
 * credential the AuthKit callback hands out. No hosted screen in between.
 *
 * Pure pieces live here — the two requests, the code normaliser and the
 * flow reducer — so the page component stays a thin renderer of `SignInFlow`.
 */
import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";

export const MAGIC_CODE_LENGTH = 6;
/** Seconds before "Resend code" is offered again. */
export const RESEND_COOLDOWN_SECONDS = 30;

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface MagicAuthFailure {
  readonly ok: false;
  readonly error: string;
  readonly message: string;
}

const endpoint = (path: string): string =>
  new URL(path, new URL(resolvePrimaryEnvironmentHttpUrl("/")).origin).toString();

/** The response body as an object, whatever the server actually sent. */
const jsonRecord = async (response: Response): Promise<Record<string, unknown>> => {
  const body: unknown = await response.json().catch(() => ({}));
  return body !== null && typeof body === "object" ? (body as Record<string, unknown>) : {};
};

const failure = async (response: Response): Promise<MagicAuthFailure> => {
  const body = await jsonRecord(response);
  return {
    ok: false,
    error: typeof body.error === "string" ? body.error : `http_${response.status}`,
    message:
      typeof body.message === "string" && body.message.length > 0
        ? body.message
        : "Sign-in did not complete. Please try again.",
  };
};

const post = async (fetchImpl: FetchLike, path: string, body: unknown): Promise<Response> =>
  fetchImpl(endpoint(path), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

const network = (): MagicAuthFailure => ({
  ok: false,
  error: "network",
  message: "Could not reach the workspace. Check your connection and try again.",
});

/** Step one: WorkOS emails a code; the server binds this browser to it. */
export async function requestMagicCode(
  email: string,
  fetchImpl: FetchLike = (input, init) => fetch(input, init),
): Promise<{ readonly ok: true; readonly state: string } | MagicAuthFailure> {
  let response: Response;
  try {
    response = await post(fetchImpl, "/api/amoreg/auth/workos/code", { email });
  } catch {
    return network();
  }
  if (!response.ok) return failure(response);
  const body = await jsonRecord(response);
  return typeof body.state === "string" && body.state.length > 0
    ? { ok: true, state: body.state }
    : { ok: false, error: "malformed", message: "Sign-in did not complete. Please try again." };
}

/** Step two: the code buys the pairing credential the page exchanges. */
export async function verifyMagicCode(
  input: { readonly email: string; readonly code: string; readonly state: string },
  fetchImpl: FetchLike = (request, init) => fetch(request, init),
): Promise<
  | { readonly ok: true; readonly credential: string; readonly workosSessionId: string | null }
  | MagicAuthFailure
> {
  let response: Response;
  try {
    response = await post(fetchImpl, "/api/amoreg/auth/workos/verify", input);
  } catch {
    return network();
  }
  if (!response.ok) return failure(response);
  const body = await jsonRecord(response);
  return typeof body.credential === "string" && body.credential.length > 0
    ? {
        ok: true,
        credential: body.credential,
        workosSessionId: typeof body.workosSessionId === "string" ? body.workosSessionId : null,
      }
    : { ok: false, error: "malformed", message: "Sign-in did not complete. Please try again." };
}

/**
 * One call in flight at a time; a call made meanwhile is dropped (resolves
 * `null`) rather than queued. A completed code submits itself, and Enter or
 * an autofill can submit the same form in the same tick: a second verify
 * would find the code already spent and bounce the person back to the email
 * step even though the first one signed them in.
 */
export function singleFlight<A extends readonly unknown[], R>(
  run: (...args: A) => Promise<R>,
): (...args: A) => Promise<R | null> {
  let inFlight = false;
  return async (...args) => {
    if (inFlight) return null;
    inFlight = true;
    try {
      return await run(...args);
    } finally {
      inFlight = false;
    }
  };
}

/** Digits only, at most the code's length — pasted "123 456" becomes 123456. */
export const normaliseMagicCode = (raw: string): string =>
  raw.replace(/\D/g, "").slice(0, MAGIC_CODE_LENGTH);

/** A loose check to stop obvious typos before a round trip; the server decides. */
export const looksLikeEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * The page's state, step by step. `busy` covers the in-flight request;
 * `hostedFallback` means WorkOS could not finish this account headlessly
 * (SSO, MFA, an organization to pick) and the hosted page is the way in.
 */
export interface SignInFlow {
  readonly step: "email" | "code";
  readonly email: string;
  readonly code: string;
  /** The server's binding for the code that was sent; empty until then. */
  readonly state: string;
  readonly busy: boolean;
  readonly error: string;
  readonly hostedFallback: boolean;
  /** Epoch ms after which a resend is offered; 0 when never sent. */
  readonly resendAfter: number;
  /** Which way the last step change went; the page slides accordingly. */
  readonly direction: "forward" | "back";
  /** Refusals so far on this code step; each one shakes the field. */
  readonly failures: number;
  /** The code was accepted; the page settles before the exchange. */
  readonly succeeded: boolean;
}

export const initialSignInFlow = (error = ""): SignInFlow => ({
  step: "email",
  email: "",
  code: "",
  state: "",
  busy: false,
  error,
  hostedFallback: false,
  resendAfter: 0,
  direction: "forward",
  failures: 0,
  succeeded: false,
});

export type SignInFlowEvent =
  | { readonly type: "email-changed"; readonly email: string }
  | { readonly type: "code-changed"; readonly code: string }
  | { readonly type: "request-started" }
  | { readonly type: "code-sent"; readonly state: string; readonly now: number }
  | { readonly type: "verify-started" }
  | { readonly type: "failed"; readonly error: string; readonly message: string }
  | { readonly type: "succeeded" }
  | { readonly type: "change-email" };

export function signInFlowReducer(flow: SignInFlow, event: SignInFlowEvent): SignInFlow {
  switch (event.type) {
    case "email-changed":
      return { ...flow, email: event.email, error: "", hostedFallback: false };
    case "code-changed":
      // An accepted code is frozen until the page moves on.
      if (flow.succeeded) return flow;
      return { ...flow, code: normaliseMagicCode(event.code), error: "" };
    case "request-started":
    case "verify-started":
      if (flow.succeeded) return flow;
      return { ...flow, busy: true, error: "", hostedFallback: false };
    case "code-sent":
      return {
        ...flow,
        step: "code",
        code: "",
        state: event.state,
        busy: false,
        error: "",
        resendAfter: event.now + RESEND_COOLDOWN_SECONDS * 1000,
        direction: "forward",
        failures: 0,
        succeeded: false,
      };
    case "failed": {
      const hosted = event.error === "use_hosted_login";
      // A spent binding or an account the grant cannot finish sends the
      // person back to the start; a mistyped code stays on the code step.
      // `login_failed` is retryable with the same code (the server kept the
      // binding); `login_incomplete` means the code was spent at WorkOS.
      const backToEmail =
        hosted ||
        event.error === "not_bound" ||
        event.error === "code_expired" ||
        event.error === "code_used" ||
        event.error === "not_a_member" ||
        event.error === "login_incomplete";
      return {
        ...flow,
        busy: false,
        error: event.message,
        hostedFallback: hosted,
        failures: flow.failures + 1,
        // A refused code is cleared so the next one is typed into empty
        // slots, not edited digit by digit over the wrong one.
        ...(event.error === "invalid_code" ? { code: "" } : {}),
        ...(backToEmail ? { step: "email", code: "", state: "", direction: "back" } : {}),
      };
    }
    case "succeeded":
      return { ...flow, busy: false, error: "", succeeded: true };
    case "change-email":
      return {
        ...flow,
        step: "email",
        code: "",
        state: "",
        error: "",
        hostedFallback: false,
        direction: "back",
        succeeded: false,
      };
  }
}

/** Whole seconds until a resend is allowed; 0 when it is. */
export const resendCooldownRemaining = (flow: SignInFlow, now: number): number =>
  Math.max(0, Math.ceil((flow.resendAfter - now) / 1000));

/**
 * Errors that mean "this server has no headless sign-in" rather than "you
 * got something wrong": the page sends the person through the hosted route
 * instead of showing them a message. A server without the new routes at all
 * answers 404 with no body, hence the raw status code too.
 */
export const isHostedOnlyError = (error: string): boolean =>
  error === "not_configured" || error === "http_404";
